/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, no-prototype-builtins, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import SubjectReportVisibility from '@linc-technologies/linced-common/enums/subject-report-visibility';
import SubjectType from '@linc-technologies/linced-common/enums/subject-type';
import { byKeys, findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class GoalsController extends Controller {
  queryParams = ['currentTab'];

  // Services
  @service
  features;

  @service
  options;

  @service
  paperToaster;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  canEdit = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get jurisdictionLabel() {
    return Jurisdiction.getLabel(this.jurisdiction);
  }

  @computed('model.subjects.@each.{subjectTypeName,isTopic,isVisible}')
  get subjectsByType() {
    let subjects = this.get('model.subjects') || []; // Ensure model exists
    let groupedSubjects = subjects.reduce((result, subject) => {
      if (!subject.isTopic && subject.isVisible) {
        let type = subject.subjectTypeName;
        if (!result[type]) {
          result[type] = [];
        }
        result[type].push(subject);
      }
      return result;
    }, {});

    // Ensure 'Goals' key exists even if empty
    if (!groupedSubjects['Goals']) {
      groupedSubjects['Goals'] = [];
    }
    return groupedSubjects;
  }

  // subjectTypeTabs provides a list of subject types to show tabs for, in a
  // stable order.
  @computed('subjectsByType')
  get subjectTypeTabs() {
    return SubjectType.values
      .filter(
        (subjectType) =>
          subjectType.value > 0 &&
          this.subjectsByType.hasOwnProperty(subjectType.label),
      )
      .sort(byKeys('priority', 'label'));
  }

  @computed('session.scopes.[]')
  get isHeroAdmin() {
    return (
      this.permissions.meet(Scopes.LINCED_ADMIN) ||
      this.permissions.meet(Scopes.LINCED_MANAGER)
    );
  }

  @computed('isHeroAdmin', 'jurisdiction')
  get availableTypes() {
    const allTypes = SubjectType.values;
    let filteredTypes;

    if (this.isHeroAdmin) {
      filteredTypes = allTypes.filter((item) => item.value);
    } else {
      filteredTypes = allTypes.filter(
        (type) =>
          type.value !== 0 &&
          type.schoolCanEdit &&
          (type.jurisdiction === this.jurisdictionLabel ||
            type.jurisdiction === 'All'),
      );
    }

    // Add the "Close menu" option
    return [
      { value: null, label: 'Close menu' }, // Special option to close the menu
      ...filteredTypes,
    ];
  }

  @computed('model.{milestones.[],milestoneSets.@each.label}')
  get milestoneSetOptions() {
    let options = this.model.milestoneSets
      .slice()
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    if (isEmpty(this.model.milestones)) {
      // The school has no Default Milestones; only offer Sets.
      return options;
    }
    options.unshift({ label: 'Default Milestones', isDefault: true });
    return options;
  }

  // Compute the tab labels for the PaperTabs component
  @computed('subjectsByType')
  get tabLabels() {
    let subjectsByType = this.subjectsByType;
    // Create an array with "Subjects", dynamic type labels, and "Hidden Subjects"
    let labels = [];
    Object.keys(subjectsByType).forEach((typeLabel) => {
      labels.push(typeLabel);
    });
    labels.push('Hidden Subjects');
    return labels;
  }

  Scopes = Scopes;

  @tracked
  subjectDialogOpen = false;

  subjectSaveInProgress = false;

  @tracked
  editingSubject = null;

  @tracked
  subjectLabel = '';

  @action
  setSubjectLabel(value) {
    this.subjectLabel = value;
  }

  @tracked
  subjectDescription = '';

  @action
  setSubjectDescription(value) {
    this.subjectDescription = value;
  }

  @tracked
  subjectTrackable = false;

  @action
  setSubjectTrackable(value) {
    this.subjectTrackable = value;
  }

  @tracked
  subjectBestFits = false;

  @action
  setSubjectBestFits(value) {
    this.subjectBestFits = value;
  }

  @tracked
  subjectVisibleToTeachers = true;

  @action
  setSubjectVisibleToTeachers(value) {
    this.subjectVisibleToTeachers = value;
  }

  @tracked
  subjectVisibleToParents = true;

  @action
  setSubjectVisibleToParents(value) {
    this.subjectVisibleToParents = value;
  }

  @tracked
  subjectVisibleToLearners = true;

  @action
  setSubjectVisibleToLearners(value) {
    this.subjectVisibleToLearners = value;
  }

  @tracked
  subjectIsGradable = false;

  @action
  setSubjectIsGradable(value) {
    this.subjectIsGradable = value;
  }

  @tracked
  subjectIsTopic = false;

  @tracked
  subjectType = '';

  @tracked
  subjectCategory = '';

  @tracked
  subjectMilestoneSet = null;

  @action
  setSubjectMilestoneSet(value) {
    this.subjectMilestoneSet = value;
  }

  @tracked
  subjectReportVisibility = SubjectReportVisibility.IF_PAGE_ON_REPORT;

  @tracked
  showRadialDials = false;

  @action
  setShowRadialDials(value) {
    this.showRadialDials = value;
  }

  // Success Criteria Language
  @tracked
  SC1 = '';

  @tracked
  SC2 = '';

  @tracked
  SC3 = '';

  @tracked
  SC4 = '';

  @tracked
  SC5 = '';

  reportVisibilityOptions = [
    { value: SubjectReportVisibility.ALWAYS, label: 'Yes' },
    { value: SubjectReportVisibility.NEVER, label: 'No' },
    {
      value: SubjectReportVisibility.IF_PAGE_ON_REPORT,
      label: "If on a Page that's visible on Reports",
    },
  ];

  subjectTypes = SubjectType.values;

  @tracked
  sortSubjectsDialogOpen;

  @computed('editingSubject.id', 'model.expectations.@each.goal_subject_id')
  get subjectMilestoneSetLocked() {
    let subjectId = this.get('editingSubject.id');
    if (isEmpty(subjectId)) {
      // New/unsaved Subject; don't lock the Milestone Set.
      return false;
    }
    // Existing Subject; lock Milestone Set if there are any Expectations.
    return !isEmpty(
      this.model.expectations.find(
        (item) => item.goal_subject_id === subjectId,
      ),
    );
  }

  @computed('model.subjects.@each.{isTopic,isVisible}')
  get subjectsToSort() {
    return this.model.subjects.filter((subject) => {
      return subject && !subject.isTopic && subject.isVisible;
    });
  }

  @action
  handleTypeSelect(selectedOption) {
    if (selectedOption.value === null) {
      // Close the dropdown without changing selection
    } else {
      // Update the selection
      this.subjectType = selectedOption;
    }
  }

  @action
  setSubjectReportVisibility(selectedOption) {
    this.subjectReportVisibility = selectedOption.value;
  }

  @action
  addSubject() {
    this.resetSubject();
    this.subjectDialogOpen = true;
  }

  @action
  sortSubjects() {
    this.sortSubjectsDialogOpen = true;
  }

  // updateSuccessCriteria writes the per-grade success-criteria label. The grade
  // is curried in by `{{fn}}` so the input's value arrives last. `SC1`-`SC5` are
  // tracked, so the computed-key assignment goes through the tracked setter.
  @action
  updateSuccessCriteria(gradeValue, value) {
    this[`SC${gradeValue}`] = value;
  }

  @action
  async editSubject(subject) {
    this.resetSubject();
    // A belongsTo takes a record, not the async proxy.
    // Without a set, grab the "Default Milestones" virtual set, if it exists.
    let milestoneSet =
      (await subject.milestoneSet) ??
      findBy(this.milestoneSetOptions, 'isDefault');
    this.editingSubject = subject;
    this.subjectLabel = subject.label;
    this.subjectDescription = subject.description;
    this.subjectTrackable = subject.trackable;
    this.subjectBestFits = subject.calculateBestFits;
    this.subjectVisibleToTeachers = subject.visibleToTeachers;
    this.subjectVisibleToParents = subject.visibleToParents;
    this.subjectVisibleToLearners = subject.visibleToLearners;
    this.subjectIsGradable = subject.isGradable;
    this.subjectIsTopic = subject.isTopic;
    this.subjectMilestoneSet = milestoneSet;
    this.subjectDialogOpen = true;
    this.showRadialDials = subject.showRadialDials;

    this.subjectType = this.subjectTypes.find(
      (item) => item.value === subject.get('subjectType'),
    );

    this.subjectReportVisibility = subject.reportVisibility;
    this.SC1 = subject.SC1;
    this.SC2 = subject.SC2;
    this.SC3 = subject.SC3;
    this.SC4 = subject.SC4;
    this.SC5 = subject.SC5;
  }
  @action
  closeSubjectDialog() {
    this.subjectDialogOpen = false;
  }

  @action
  closeSortSubjectsDialog() {
    this.sortSubjectsDialogOpen = false;
  }

  @action
  async saveSubject() {
    let subject = this.editingSubject;
    let toaster = this.paperToaster;

    if (isEmpty(subject)) {
      subject = this.store.createRecord('goals/subject', {
        schoolId: this.get('session.school'),
      });
    }

    // Catch all errors. A throw here makes PaperForm submit the native form,
    // and the page reloads.
    try {
      subject.setProperties({
        label: this.subjectLabel,
        description: this.subjectDescription,
        trackable: this.subjectTrackable,
        calculateBestFits: this.subjectBestFits,
        visibleToTeachers: this.subjectVisibleToTeachers,
        visibleToParents: this.subjectVisibleToParents,
        visibleToLearners: this.subjectVisibleToLearners,
        showRadialDials: this.showRadialDials,
        isGradable: this.subjectIsGradable,
        isTopic: this.subjectIsTopic,
        subjectType: this.subjectType.value,
        subjectCategory: this.subjectCategory,
        // "Default Milestones" is not a record. A belongsTo takes a record or null.
        milestoneSet: this.subjectMilestoneSet?.isDefault
          ? null
          : this.subjectMilestoneSet,
        reportVisibility: this.subjectReportVisibility,
        SC1: this.SC1,
        SC2: this.SC2,
        SC3: this.SC3,
        SC4: this.SC4,
        SC5: this.SC5,
      });

      await subject.save();
      this.subjectDialogOpen = false;
      toaster.show(`Goals subject "${subject.get('label')}" saved.`, {
        duration: 4000,
        position: 'top right',
      });
    } catch {
      toaster.show(`Error saving goals subject.`, {
        duration: 4000,
        position: 'top right',
      });
    }
  }

  resetSubject() {
    this.editingSubject = null;
    this.subjectLabel = '';
    this.subjectDescription = '';
    this.subjectTrackable = false;
    this.subjectBestFits = false;
    this.subjectVisibleToTeachers = true;
    this.subjectVisibleToParents = true;
    this.subjectVisibleToLearners = true;
    this.subjectIsGradable = false;
    this.subjectIsTopic = false;
    this.showRadialDials = false;
    this.subjectType = { value: 0, label: 'Goals' };
    this.subjectCategory = '';
    this.subjectMilestoneSet = null;
    this.subjectReportVisibility = SubjectReportVisibility.IF_PAGE_ON_REPORT;
    this.SC1 = '';
    this.SC2 = '';
    this.SC3 = '';
    this.SC4 = '';
    this.SC5 = '';
  }
}
